"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { FieldHint } from "@/components/ui";
import { apiPost, ApiClientError } from "@/lib/api-client";
import type { SafeUser } from "@/types/auth";

function GoogleIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.6-.2-2.36H12v4.47h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.74Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.9l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.09A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.28a12 12 0 0 0 0 10.76l3.99-3.09Z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.28 6.62l3.99 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
    </svg>
  );
}

/** Đăng nhập Google qua Firebase → gửi ID token cho backend → về trang đích. */
export function GoogleFirebaseButton({ next, onSuccess }: { next?: string; onSuccess?: (user: SafeUser) => void }) {
  const router = useRouter();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleClick() {
    setError(null);
    setLoading(true);
    try {
      // SDK Firebase (~45kB gzip) chỉ cần SAU khi bấm nút → nạp trễ để trang
      // đăng nhập/đăng ký không phải tải nó ngay từ đầu. Trạng thái `loading`
      // đã bật trước đó nên người dùng thấy spinner trong lúc chunk đang tải.
      const { signInWithGoogleGetIdToken } = await import("@/lib/firebase/client");
      const idToken = await signInWithGoogleGetIdToken();
      const { user } = await apiPost<{ user: SafeUser }>("/api/auth/firebase", { idToken });
      if (onSuccess) {
        onSuccess(user);
        router.refresh();
      } else {
        router.push(next || "/bang-dieu-khien");
        router.refresh();
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err && typeof err === "object" && "code" in err) {
        const code = String((err as { code: unknown }).code);
        // Người dùng tự đóng popup → không coi là lỗi.
        if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
          setLoading(false);
          return;
        }
        setError("Không đăng nhập được với Google. Vui lòng thử lại.");
      } else {
        setError("Không đăng nhập được với Google. Vui lòng thử lại.");
      }
      setLoading(false);
    }
  }

  // Rê chuột / focus vào nút → nạp trước chunk Firebase, nên lúc bấm thật gần
  // như không phải chờ. Nuốt lỗi: đây chỉ là tối ưu, handleClick vẫn tự nạp lại.
  function prefetchFirebase() {
    void import("@/lib/firebase/client").catch(() => {});
  }

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={handleClick}
        onPointerEnter={prefetchFirebase}
        onFocus={prefetchFirebase}
        disabled={loading}
        className="flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-border-strong bg-surface text-body-sm font-semibold text-foreground transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring focus-visible:ring-ring disabled:opacity-60"
      >
        {loading ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <GoogleIcon />}
        Đăng nhập với Google
      </button>
      {error && <FieldHint error>{error}</FieldHint>}
    </div>
  );
}
